import React, { useEffect, useState } from "react";
import { adminApi, formatErr } from "./api";
import { useEscapeKey } from "./hooks";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Users, Phone, Mail, BookOpen, IndianRupee, PackageCheck, RotateCcw, TrendingUp, Wallet, Calendar, Boxes, Zap } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const empty = {
  name: "", city: "", state: "", gst_number: "", contact_person: "",
  phone: "", email: "", credit_limit: 0, outstanding_amount: 0, active: true,
  held_qty: 0, dispatched_qty: 0, sold_qty: 0,
};

const emptyStock = { held_qty: 0, dispatched_qty: 0, sold_qty: 0, note: "" };

export default function AdminDistributors() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [velocity, setVelocity] = useState(null);
  const [velocitySummary, setVelocitySummary] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 0, payment_date: new Date().toISOString().slice(0, 10), mode: "bank_transfer", reference: "", notes: "" });
  const [payBusy, setPayBusy] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [stockTarget, setStockTarget] = useState(null);
  const [stockForm, setStockForm] = useState(emptyStock);
  const [stockBusy, setStockBusy] = useState(false);

  // ESC-to-close — innermost dialog first so layered modals stack correctly
  useEscapeKey(stockOpen, () => setStockOpen(false));
  useEscapeKey(payOpen, () => setPayOpen(false));
  useEscapeKey(open, () => setOpen(false));
  useEscapeKey(!!ledger && !payOpen && !stockOpen, () => setLedger(null));

  const openStockDialog = (d) => {
    setStockTarget(d);
    setStockForm({
      held_qty: d.held_qty || 0,
      dispatched_qty: d.dispatched_qty || 0,
      sold_qty: d.sold_qty || 0,
      note: "",
    });
    setStockOpen(true);
  };
  const setStock = (k) => (e) => setStockForm({ ...stockForm, [k]: e.target.value });

  const saveStock = async (e) => {
    e.preventDefault();
    if (!stockTarget) return;
    const payload = {
      held_qty: Number(stockForm.held_qty),
      dispatched_qty: Number(stockForm.dispatched_qty),
      sold_qty: Number(stockForm.sold_qty),
      note: stockForm.note || undefined,
    };
    if ([payload.held_qty, payload.dispatched_qty, payload.sold_qty].some((v) => isNaN(v) || v < 0)) {
      toast.error("Quantities cannot be negative");
      return;
    }
    setStockBusy(true);
    try {
      await adminApi.patch(`/distributors/${stockTarget.id}/stock`, payload);
      toast.success("Stock allocation updated");
      setStockOpen(false);
      await Promise.all([load(), loadVelocitySummary()]);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setStockBusy(false); }
  };

  const load = async () => {
    const { data } = await adminApi.get("/distributors");
    setItems(data.distributors);
  };
  useEffect(() => { load(); loadVelocitySummary(); }, []);

  const loadVelocitySummary = async () => {
    try {
      const { data } = await adminApi.get("/distributors-velocity-summary");
      setVelocitySummary(data.distributors || []);
    } catch { /* non-critical */ }
  };

  const openLedger = async (d) => {
    setLedger({ distributor: d, movements: [], payments: [], summary: null });
    setLedgerLoading(true);
    setVelocity(null);
    try {
      const [ledgerRes, velocityRes] = await Promise.all([
        adminApi.get(`/distributors/${d.id}/ledger`),
        adminApi.get(`/distributors/${d.id}/velocity?weeks=8`),
      ]);
      setLedger(ledgerRes.data);
      setVelocity(velocityRes.data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
      setLedger(null);
    } finally { setLedgerLoading(false); }
  };

  const refreshLedger = async () => {
    if (!ledger?.distributor?.id) return;
    const { data } = await adminApi.get(`/distributors/${ledger.distributor.id}/ledger`);
    setLedger(data);
  };

  const setPay = (k) => (e) => setPayForm({ ...payForm, [k]: e.target.value });

  const savePayment = async (e) => {
    e.preventDefault();
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a payment amount"); return; }
    setPayBusy(true);
    try {
      const payload = { ...payForm, amount: amt };
      if (!payload.reference) delete payload.reference;
      if (!payload.notes) delete payload.notes;
      await adminApi.post(`/distributors/${ledger.distributor.id}/payments`, payload);
      toast.success(`Payment of ₹${amt.toLocaleString("en-IN")} recorded`);
      setPayOpen(false);
      setPayForm({ amount: 0, payment_date: new Date().toISOString().slice(0, 10), mode: "bank_transfer", reference: "", notes: "" });
      await Promise.all([refreshLedger(), load()]);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setPayBusy(false); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d.id);
    setForm({ ...empty, ...d });
    setOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        credit_limit: Number(form.credit_limit) || 0,
        outstanding_amount: Number(form.outstanding_amount) || 0,
        held_qty: Number(form.held_qty) || 0,
        dispatched_qty: Number(form.dispatched_qty) || 0,
        sold_qty: Number(form.sold_qty) || 0,
        active: !!form.active,
      };
      if (editing) await adminApi.put(`/distributors/${editing}`, payload);
      else await adminApi.post("/distributors", payload);
      toast.success(editing ? "Distributor updated" : "Distributor added");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete distributor "${name}"?`)) return;
    try {
      await adminApi.delete(`/distributors/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const filtered = search
    ? items.filter((d) => `${d.name} ${d.city} ${d.state} ${d.gst_number || ""}`.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="space-y-6" data-testid="admin-distributors-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-gold">Channel partners</p>
          <h1 className="font-serif text-4xl text-bone mt-1">Distributors</h1>
        </div>
        <div className="flex gap-3 items-center">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="bg-ink-900 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
            data-testid="distributor-search-input"
          />
          <button onClick={openNew} className="btn-primary !py-3" data-testid="new-distributor-btn">
            <Plus className="w-3.5 h-3.5" /> New distributor
          </button>
        </div>
      </div>

      {/* Sell-through insights band — slowest movers */}
      {velocitySummary.length > 0 && (
        <div className="bg-ink-900 border border-white/5 p-5" data-testid="sell-through-insights">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="label-eyebrow text-gold inline-flex items-center gap-1.5"><Zap className="w-3 h-3" /> Sell-through velocity</p>
              <p className="text-mist text-xs font-light mt-1">Sold ÷ (Held + Dispatched + Sold). Slowest movers first — reallocate stock to faster channels.</p>
            </div>
            <p className="num-display text-lg text-bone">{velocitySummary.length} active</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {velocitySummary.slice(0, 8).map((v) => {
              const pct = v.sell_through_pct;
              const tone = pct >= 60 ? "text-emerald-400 border-emerald-400/40" : pct >= 30 ? "text-amber-400 border-amber-400/40" : "text-rose-300 border-rose-300/40";
              const label = pct >= 60 ? "Fast" : pct >= 30 ? "Steady" : "Slow";
              return (
                <button
                  key={v.id}
                  onClick={() => openLedger(items.find((it) => it.id === v.id) || v)}
                  className={`text-left bg-ink-950 border ${tone.split(" ")[1]} p-3 hover:bg-white/5 transition-colors`}
                  data-testid={`velocity-card-${v.id}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-bone text-sm font-light truncate">{v.name}</p>
                    <span className={`label-eyebrow ${tone.split(" ")[0]} text-[10px]`}>{label}</span>
                  </div>
                  <p className="text-mist text-[10px]">{v.city}</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <p className={`num-display text-2xl ${tone.split(" ")[0]}`}>{pct}%</p>
                    <p className="text-mist text-[10px] font-light">sold-through</p>
                  </div>
                  <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${pct >= 60 ? "bg-emerald-400" : pct >= 30 ? "bg-amber-400" : "bg-rose-300"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <p className="text-mist text-[10px] font-light mt-2">H {v.held} · D {v.dispatched} · S {v.sold}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="distributors-table">
        <table className="w-full text-sm">
          <thead className="bg-ink-950">
            <tr className="text-left label-eyebrow text-mist">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">GST</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Held</th>
              <th className="px-4 py-3">Dispatched</th>
              <th className="px-4 py-3">Sold</th>
              <th className="px-4 py-3">Sell-through</th>
              <th className="px-4 py-3">Credit limit</th>
              <th className="px-4 py-3">Outstanding</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => {
              const overdrawn = d.outstanding_amount > d.credit_limit && d.credit_limit > 0;
              return (
                <tr key={d.id} className="border-t border-white/5 text-bone/90 font-light" data-testid={`distributor-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-bone">{d.name}</p>
                    {d.contact_person && <p className="text-mist text-xs">{d.contact_person}</p>}
                  </td>
                  <td className="px-4 py-3 text-mist text-xs">{d.city}, {d.state}</td>
                  <td className="px-4 py-3 font-mono text-xs">{d.gst_number || "—"}</td>
                  <td className="px-4 py-3 text-mist text-xs">
                    {d.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</div>}
                    {d.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{d.email}</div>}
                  </td>
                  <td className="px-4 py-3 num-display text-amber-400" data-testid={`held-qty-${d.id}`}>{(d.held_qty || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 num-display text-sky-400" data-testid={`dispatched-qty-${d.id}`}>{(d.dispatched_qty || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 num-display text-emerald-400" data-testid={`sold-qty-${d.id}`}>{(d.sold_qty || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3" data-testid={`sell-through-${d.id}`}>
                    {(() => {
                      const total = (d.held_qty || 0) + (d.dispatched_qty || 0) + (d.sold_qty || 0);
                      const pct = total > 0 ? Math.round(((d.sold_qty || 0) / total) * 1000) / 10 : 0;
                      const tone = pct >= 60 ? "text-emerald-400" : pct >= 30 ? "text-amber-400" : total > 0 ? "text-rose-300" : "text-mist";
                      const bg = pct >= 60 ? "bg-emerald-400" : pct >= 30 ? "bg-amber-400" : "bg-rose-300";
                      return (
                        <div className="flex items-center gap-2 min-w-[110px]">
                          <span className={`num-display text-sm ${tone}`}>{total > 0 ? `${pct}%` : "—"}</span>
                          {total > 0 && (
                            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div className={`h-full ${bg}`} style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 num-display">₹{Math.round(d.credit_limit).toLocaleString("en-IN")}</td>
                  <td className={`px-4 py-3 num-display ${overdrawn ? "text-rose-300" : "text-gold"}`}>₹{Math.round(d.outstanding_amount).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3"><span className={`label-eyebrow ${d.active ? "text-gold" : "text-mist"}`}>{d.active ? "Active" : "Inactive"}</span></td>
                  <td className="px-4 py-3 flex gap-2 justify-end">
                    <button onClick={() => openStockDialog(d)} className="text-mist hover:text-gold p-1.5" title="Update stock allocations" data-testid={`stock-distributor-${d.id}`}><Boxes className="w-3.5 h-3.5" /></button>
                    <button onClick={() => openLedger(d)} className="text-mist hover:text-gold p-1.5" title="View ledger" data-testid={`ledger-distributor-${d.id}`}><BookOpen className="w-3.5 h-3.5" /></button>
                    <button onClick={() => openEdit(d)} className="text-mist hover:text-gold p-1.5" data-testid={`edit-distributor-${d.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(d.id, d.name)} className="text-mist hover:text-rose-300 p-1.5" data-testid={`delete-distributor-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-12 text-center text-mist font-light italic">
                <Users className="w-6 h-6 mx-auto mb-2 opacity-40" />
                No distributors {search ? "match your search" : "yet"}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {ledger && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div onClick={(e) => e.stopPropagation()} className="bg-ink-900 border border-white/10 w-full max-w-5xl p-8 space-y-6 max-h-[92vh] overflow-y-auto" data-testid="distributor-ledger-dialog">
            <div className="flex justify-between items-start gap-4">
              <div>
                <p className="label-eyebrow text-gold">Distributor ledger</p>
                <h3 className="font-serif text-3xl text-bone mt-1">{ledger.distributor?.name}</h3>
                <p className="text-mist text-xs font-light mt-1">
                  {ledger.distributor?.city}, {ledger.distributor?.state}
                  {ledger.distributor?.contact_person ? ` · ${ledger.distributor.contact_person}` : ""}
                  {ledger.distributor?.phone ? ` · ${ledger.distributor.phone}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPayOpen(true)}
                  className="btn-primary !py-2.5 !px-4 text-xs"
                  data-testid="record-payment-btn"
                >
                  <Wallet className="w-3.5 h-3.5" /> Record payment
                </button>
                <button onClick={() => setLedger(null)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
              </div>
            </div>

            {ledgerLoading && <p className="text-mist">Loading…</p>}

            {ledger.summary && (
              <>
                {/* Stock allocation chips */}
                <div className="grid grid-cols-3 gap-3" data-testid="ledger-stock-allocations">
                  <div className="bg-ink-950 border border-amber-400/30 p-4">
                    <p className="label-eyebrow text-amber-400 text-[10px]">Held qty</p>
                    <p className="num-display text-2xl text-amber-400 mt-2">{(ledger.distributor?.held_qty || 0).toLocaleString("en-IN")}</p>
                    <p className="text-mist text-[10px] font-light">reserved · not dispatched</p>
                  </div>
                  <div className="bg-ink-950 border border-sky-400/30 p-4">
                    <p className="label-eyebrow text-sky-400 text-[10px]">Dispatched qty</p>
                    <p className="num-display text-2xl text-sky-400 mt-2">{(ledger.distributor?.dispatched_qty || 0).toLocaleString("en-IN")}</p>
                    <p className="text-mist text-[10px] font-light">in transit / with distributor</p>
                  </div>
                  <div className="bg-ink-950 border border-emerald-400/30 p-4">
                    <p className="label-eyebrow text-emerald-400 text-[10px]">Sold qty</p>
                    <p className="num-display text-2xl text-emerald-400 mt-2">{(ledger.distributor?.sold_qty || 0).toLocaleString("en-IN")}</p>
                    <p className="text-mist text-[10px] font-light">sold-through to customers</p>
                  </div>
                </div>

                {/* Velocity chart */}
                {velocity && velocity.weeks && velocity.weeks.length > 0 && (
                  <div className="bg-ink-950 border border-white/5 p-4" data-testid="velocity-chart">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="label-eyebrow text-gold inline-flex items-center gap-1.5"><Zap className="w-3 h-3" /> Held → Sold velocity</p>
                        <p className="text-mist text-[10px] font-light mt-0.5">Last {velocity.weeks.length} weeks · sell-through {velocity.current.sell_through_pct}%</p>
                      </div>
                      <div className="text-right">
                        <p className="num-display text-lg text-emerald-400">+{velocity.period_sold_delta}</p>
                        <p className="text-mist text-[10px] font-light">sold this window</p>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={velocity.weeks} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="velHeld" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.6}/>
                            <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="velDisp" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.6}/>
                            <stop offset="95%" stopColor="#38BDF8" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="velSold" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#34D399" stopOpacity={0.6}/>
                            <stop offset="95%" stopColor="#34D399" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="label" stroke="#A39D98" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#A39D98" tick={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: "#1A1717", border: "1px solid rgba(255,255,255,0.1)", color: "#F9F6F0" }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Area type="monotone" dataKey="held" stroke="#F59E0B" fill="url(#velHeld)" strokeWidth={2} />
                        <Area type="monotone" dataKey="dispatched" stroke="#38BDF8" fill="url(#velDisp)" strokeWidth={2} />
                        <Area type="monotone" dataKey="sold" stroke="#34D399" fill="url(#velSold)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="bg-ink-950 border border-white/5 p-4">
                    <p className="label-eyebrow text-mist text-[10px] flex items-center gap-1.5"><PackageCheck className="w-3 h-3 text-emerald-400" /> Shipped</p>
                    <p className="num-display text-2xl text-bone mt-2">{ledger.summary.units_shipped.toLocaleString("en-IN")}</p>
                    <p className="text-mist text-[10px] font-light">units</p>
                  </div>
                  <div className="bg-ink-950 border border-white/5 p-4">
                    <p className="label-eyebrow text-mist text-[10px] flex items-center gap-1.5"><RotateCcw className="w-3 h-3 text-sky-400" /> Returned</p>
                    <p className="font-serif text-2xl text-bone mt-2">{ledger.summary.units_returned.toLocaleString("en-IN")}</p>
                    <p className="text-mist text-[10px] font-light">units</p>
                  </div>
                  <div className="bg-ink-950 border border-gold/30 p-4">
                    <p className="label-eyebrow text-gold text-[10px] flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Net value</p>
                    <p className="num-display text-2xl text-gold mt-2">₹{Math.round(ledger.summary.net_value).toLocaleString("en-IN")}</p>
                    <p className="text-mist text-[10px] font-light">{ledger.summary.net_units} units · estimated</p>
                  </div>
                  <div className="bg-ink-950 border border-emerald-400/30 p-4" data-testid="payments-kpi">
                    <p className="label-eyebrow text-emerald-400 text-[10px] flex items-center gap-1.5"><Wallet className="w-3 h-3" /> Payments</p>
                    <p className="num-display text-2xl text-emerald-400 mt-2">₹{Math.round(ledger.summary.total_payments || 0).toLocaleString("en-IN")}</p>
                    <p className="text-mist text-[10px] font-light">{ledger.summary.payments_count || 0} received</p>
                  </div>
                  <div className="bg-ink-950 border border-white/5 p-4">
                    <p className="label-eyebrow text-mist text-[10px]">Credit limit</p>
                    <p className="num-display text-2xl text-bone mt-2">₹{Math.round(ledger.summary.credit_limit).toLocaleString("en-IN")}</p>
                  </div>
                  <div className={`bg-ink-950 border p-4 ${ledger.summary.outstanding_amount > ledger.summary.credit_limit && ledger.summary.credit_limit > 0 ? "border-burgundy" : "border-white/5"}`}>
                    <p className="label-eyebrow text-mist text-[10px]">Outstanding</p>
                    <p className={`num-display text-2xl mt-2 ${ledger.summary.outstanding_amount > ledger.summary.credit_limit && ledger.summary.credit_limit > 0 ? "text-rose-300" : "text-bone"}`}>
                      ₹{Math.round(ledger.summary.outstanding_amount).toLocaleString("en-IN")}
                    </p>
                    <p className="text-mist text-[10px] font-light">
                      Available: ₹{Math.round(ledger.summary.credit_available).toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>

                <div className="bg-ink-950 border border-white/5 overflow-x-auto" data-testid="ledger-movements-table">
                  <table className="w-full text-sm">
                    <thead className="bg-ink-900">
                      <tr className="text-left label-eyebrow text-mist">
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">SKU · Product</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3 text-right">Qty</th>
                        <th className="px-4 py-3 text-right">Unit ₹</th>
                        <th className="px-4 py-3 text-right">Value</th>
                        <th className="px-4 py-3">Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.movements.map((m) => (
                        <tr key={m.id} className="border-t border-white/5 text-bone/90 font-light">
                          <td className="px-4 py-3 text-mist text-xs whitespace-nowrap">{new Date(m.created_at).toLocaleDateString("en-IN")}</td>
                          <td className="px-4 py-3"><span className="font-mono text-xs text-mist">{m.sku}</span> · {m.product_name}</td>
                          <td className={`px-4 py-3 label-eyebrow text-xs ${m.movement_type === "stock_out" ? "text-rose-300" : m.movement_type === "return" ? "text-sky-400" : "text-mist"}`}>{m.movement_type.replace("_", " ")}</td>
                          <td className="px-4 py-3 text-right">{m.quantity}</td>
                          <td className="px-4 py-3 text-right text-mist text-xs">₹{Math.round(m.unit_price || 0).toLocaleString("en-IN")}</td>
                          <td className="px-4 py-3 text-right text-gold">₹{Math.round(m.estimated_value || 0).toLocaleString("en-IN")}</td>
                          <td className="px-4 py-3 text-mist text-xs">{m.reference || "—"}</td>
                        </tr>
                      ))}
                      {ledger.movements.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-10 text-center text-mist italic font-light">No stock movements have ever been routed to this distributor.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Payments table */}
                <div>
                  <p className="label-eyebrow text-emerald-400 mb-3 mt-2 flex items-center gap-2"><Wallet className="w-3.5 h-3.5" /> Payments received</p>
                  <div className="bg-ink-950 border border-white/5 overflow-x-auto" data-testid="ledger-payments-table">
                    <table className="w-full text-sm">
                      <thead className="bg-ink-900">
                        <tr className="text-left label-eyebrow text-mist">
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Mode</th>
                          <th className="px-4 py-3">Reference</th>
                          <th className="px-4 py-3">Notes</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3">Recorded by</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ledger.payments || []).map((p) => (
                          <tr key={p.id} className="border-t border-white/5 text-bone/90 font-light" data-testid={`payment-row-${p.id}`}>
                            <td className="px-4 py-3 text-mist text-xs whitespace-nowrap">{p.payment_date}</td>
                            <td className="px-4 py-3 label-eyebrow text-xs text-gold">{p.mode?.replace("_", " ")}</td>
                            <td className="px-4 py-3 font-mono text-xs">{p.reference || "—"}</td>
                            <td className="px-4 py-3 text-mist text-xs">{p.notes || "—"}</td>
                            <td className="px-4 py-3 text-right text-emerald-400">₹{Math.round(p.amount).toLocaleString("en-IN")}</td>
                            <td className="px-4 py-3 text-mist text-xs">{p.created_by}</td>
                          </tr>
                        ))}
                        {(!ledger.payments || ledger.payments.length === 0) && (
                          <tr><td colSpan={6} className="px-4 py-8 text-center text-mist italic font-light">No payments recorded yet — use &ldquo;Record payment&rdquo; above.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {payOpen && (
                  <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6">
                    <form onClick={(e) => e.stopPropagation()} onSubmit={savePayment} className="bg-ink-900 border border-emerald-400/40 w-full max-w-lg p-8 space-y-4" data-testid="payment-form-dialog">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="label-eyebrow text-emerald-400">Record payment</p>
                          <h3 className="font-serif text-2xl text-bone mt-1">{ledger.distributor.name}</h3>
                        </div>
                        <button type="button" onClick={() => setPayOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
                      </div>
                      <p className="text-mist text-xs font-light">
                        Current outstanding: <span className="text-bone">₹{Math.round(ledger.summary.outstanding_amount).toLocaleString("en-IN")}</span>
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="col-span-2 text-xs text-mist">Amount (₹)
                          <input
                            required
                            type="number"
                            min="1"
                            step="0.01"
                            value={payForm.amount}
                            onChange={setPay("amount")}
                            className="mt-1 w-full bg-ink-950 border border-emerald-400/30 px-3 py-3 text-lg font-light text-bone outline-none focus:border-emerald-400"
                            data-testid="payment-amount-input"
                          />
                        </label>
                        <label className="text-xs text-mist">Payment date
                          <input
                            required
                            type="date"
                            value={payForm.payment_date}
                            onChange={setPay("payment_date")}
                            className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                            data-testid="payment-date-input"
                          />
                        </label>
                        <label className="text-xs text-mist">Mode
                          <select
                            value={payForm.mode}
                            onChange={setPay("mode")}
                            className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                            data-testid="payment-mode-select"
                          >
                            <option value="bank_transfer">Bank transfer</option>
                            <option value="upi">UPI</option>
                            <option value="cheque">Cheque</option>
                            <option value="cash">Cash</option>
                            <option value="other">Other</option>
                          </select>
                        </label>
                        <input
                          value={payForm.reference}
                          onChange={setPay("reference")}
                          placeholder="Reference (UTR / cheque #)"
                          className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                          data-testid="payment-reference-input"
                        />
                        <textarea
                          value={payForm.notes}
                          onChange={setPay("notes")}
                          placeholder="Notes (optional)"
                          rows={2}
                          className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none"
                        />
                      </div>
                      <div className="flex justify-end gap-3 pt-3">
                        <button type="button" onClick={() => setPayOpen(false)} className="btn-ghost !py-3">Cancel</button>
                        <button
                          type="submit"
                          disabled={payBusy}
                          className="!py-3 !px-6 text-xs label-eyebrow bg-emerald-500/20 border border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/30 transition-colors flex items-center gap-2"
                          data-testid="save-payment-btn"
                        >
                          <Wallet className="w-3 h-3" />
                          {payBusy ? "Saving…" : `Record ₹${Math.round(Number(payForm.amount) || 0).toLocaleString("en-IN")}`}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-ink-900 border border-white/10 w-full max-w-2xl p-8 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="distributor-form-dialog">
            <div className="flex justify-between items-start">
              <div>
                <p className="label-eyebrow text-gold">{editing ? "Edit distributor" : "New distributor"}</p>
                <h3 className="font-serif text-2xl text-bone mt-1">Channel partner details</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-mist hover:text-gold mt-1" aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input required value={form.name} onChange={set("name")} placeholder="Business name" className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="distributor-name-input" />
              <input required value={form.city} onChange={set("city")} placeholder="City" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input required value={form.state} onChange={set("state")} placeholder="State" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input value={form.gst_number} onChange={set("gst_number")} placeholder="GST number" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input value={form.contact_person} onChange={set("contact_person")} placeholder="Contact person" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input type="email" value={form.email} onChange={set("email")} placeholder="Email" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <label className="block text-xs text-mist">Credit limit (₹)
                <input type="number" value={form.credit_limit} onChange={set("credit_limit")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              </label>
              <label className="block text-xs text-mist">Outstanding (₹)
                <input type="number" value={form.outstanding_amount} onChange={set("outstanding_amount")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              </label>
              <label className="block text-xs text-mist">Held qty
                <input type="number" min="0" value={form.held_qty} onChange={set("held_qty")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="distributor-held-input" />
              </label>
              <label className="block text-xs text-mist">Dispatched qty
                <input type="number" min="0" value={form.dispatched_qty} onChange={set("dispatched_qty")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="distributor-dispatched-input" />
              </label>
              <label className="block text-xs text-mist col-span-2">Sold qty
                <input type="number" min="0" value={form.sold_qty} onChange={set("sold_qty")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="distributor-sold-input" />
              </label>
              <label className="col-span-2 flex items-center gap-2 text-bone text-sm font-light">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-gold" />
                Active partner
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary !py-3" data-testid="save-distributor-btn">{busy ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Stock allocation dialog */}
      {stockOpen && stockTarget && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6">
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveStock} className="bg-ink-900 border border-gold/40 w-full max-w-lg p-8 space-y-4" data-testid="distributor-stock-dialog">
            <div className="flex justify-between items-center">
              <div>
                <p className="label-eyebrow text-gold">Update stock allocations</p>
                <h3 className="font-serif text-2xl text-bone mt-1">{stockTarget.name}</h3>
                <p className="text-mist text-xs font-light mt-0.5">{stockTarget.city}, {stockTarget.state}</p>
              </div>
              <button type="button" onClick={() => setStockOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-mist text-xs font-light">
              Held = reserved against this distributor but not yet shipped · Dispatched = goods shipped, awaiting sell-through · Sold = sold through to end customers.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs text-mist">Held
                <input type="number" min="0" required value={stockForm.held_qty} onChange={setStock("held_qty")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-lg font-light text-amber-400 outline-none focus:border-gold" data-testid="held-qty-input" />
              </label>
              <label className="text-xs text-mist">Dispatched
                <input type="number" min="0" required value={stockForm.dispatched_qty} onChange={setStock("dispatched_qty")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-lg font-light text-sky-400 outline-none focus:border-gold" data-testid="dispatched-qty-input" />
              </label>
              <label className="text-xs text-mist">Sold
                <input type="number" min="0" required value={stockForm.sold_qty} onChange={setStock("sold_qty")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-lg font-light text-emerald-400 outline-none focus:border-gold" data-testid="sold-qty-input" />
              </label>
            </div>
            <textarea value={stockForm.note} onChange={setStock("note")} placeholder="Note (optional — appended to stock log)" rows={2} className="w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none" />
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setStockOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={stockBusy} className="btn-primary !py-3" data-testid="save-stock-btn">{stockBusy ? "Saving…" : "Save allocation"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
